import { useEffect, useMemo, useRef } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import useScreenSize from '@/hooks/useScreenSize';
import VerifyGalleryArtworkCard from '@/shared-components/cards/components/VerifyGalleryArtworkCard';
import ForwardArrowIcon from '@/shared-components/icons/components/public/ForwardArrowIcon';
import Text from '@/shared-components/text/components/Text';

import './index.css';

// Normalize status to a canonical value
const normalizeStatus = (status) => {
  const normalized = (status || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s|-/g, '_');
  if (normalized === 'changes' || normalized === 'changes_required') return 'changes_required';
  if (
    normalized === 'pending' ||
    normalized === 'under_review' ||
    normalized === 'in_review' ||
    normalized === 'review'
  ) {
    return 'pending';
  }
  if (normalized === 'rejected' || normalized === 'declined') return 'rejected';
  if (normalized === 'approved' || normalized === 'accepted' || normalized === 'verified') {
    return 'verified';
  }
  if (normalized === 'incomplete') return 'incomplete';
  // Fall back to the raw value so unknown statuses keep a stable order at the end.
  return normalized || 'verified';
};

const STATUS_PRIORITY = {
  changes_required: 1,
  pending: 2,
  verified: 3,
  rejected: 4,
};

const getItemStatus = (item) =>
  item?.draft_status ?? item?.status ?? item?.artwork_status;

// Remove duplicates by id or fallback key
const dedupeById = (items) => {
  const seen = new Set();
  const result = [];
  items.forEach((item) => {
    const keyCandidates = [
      item?._id,
      item?.id,
      item?.draft_id,
      item?.artwork_id,
      item?.draft_container?.draft_id,
      item?.draft_container?.artwork_id,
    ].filter(Boolean);
    const fallbackKey = `${
      item?.draft_container?.artwork_media?.artwork_main_picture?.image_id ?? ''
    }::${item?.draft_container?.artwork_about?.artwork_title ?? ''}`;
    const dedupeKey = keyCandidates[0] ?? fallbackKey;
    if (!seen.has(dedupeKey)) {
      seen.add(dedupeKey);
      result.push(item);
    }
  });
  return result;
};

export default function UploadedArtworks() {
  // 👇 ahora recibimos el callback para reportar el número de pending
  const { fetchData, sortedItems, reportPendingCount } = useOutletContext();

  const draftsCount = useMemo(
    () => fetchData.drafts.filter(d => d.draft_status === 'incomplete').length,
    [fetchData]
  );

  // Lista a renderizar: usar el merged y ordenado globalmente desde el parent.
  // Fallback: si no llega sortedItems, mezclamos y ordenamos aquí.
  const displayItems = useMemo(() => {
    const baseItems =
      Array.isArray(sortedItems) && sortedItems.length > 0
        ? sortedItems
        : [...(fetchData?.drafts || []), ...(fetchData?.artworks || [])];
    const uploadedItems = baseItems.filter(
      (item) => normalizeStatus(getItemStatus(item)) !== 'incomplete'
    );
    return dedupeById(uploadedItems).sort((a, b) => {
      const priorityA = STATUS_PRIORITY[normalizeStatus(getItemStatus(a))] ?? 99;
      const priorityB = STATUS_PRIORITY[normalizeStatus(getItemStatus(b))] ?? 99;
      return priorityA - priorityB;
    });
  }, [fetchData, sortedItems]);

  // Contar solo "pending/under review" para el reporte al parent
  const pendingCount = useMemo(() => {
    const isPending = (item) => normalizeStatus(getItemStatus(item)) === 'pending';
    return displayItems.filter(isPending).length;
  }, [displayItems]);

  const screenSize = useScreenSize();
  const listRef = useRef();

  // Card gap for spacing between rows
  const CARD_GAP = 10;
  const ROW_HEIGHT = screenSize.width <= 1004 ? 155 + CARD_GAP : 228 + CARD_GAP;

  // Recalcular altura de filas al cambiar el ancho
  useEffect(() => {
    listRef.current?.recomputeRowHeights();
  }, [screenSize.width]);

  // 👇 Reportamos al parent cuántas obras "pending" hay (las que renderizamos aquí)
  useEffect(() => {
    reportPendingCount?.(pendingCount);
  }, [pendingCount, reportPendingCount]);

  return (
    <>
      {draftsCount > 0 ? (
        <Link
          to="/gallery-onboarding/drafts"
          state={{ from: true }}
          className="uploaded-artworks__drafts-container"
        >
          <Text className="uploaded-artworks__draft-counter font-size-large">
            Drafts <span className="drafts-number">({draftsCount})</span>
          </Text>
          <ForwardArrowIcon className="uploaded-artworks__drafts-enter-icon" />
        </Link>
      ) : (
        <div className="uploaded-artworks__drafts-container">
          <Text className="uploaded-artworks__draft-counter font-size-large">
            Drafts <span className="drafts-number">(0)</span>
          </Text>
        </div>
      )}
      <WindowScroller>
        {({ height, isScrolling, onChildScroll, scrollTop }) => (
          <AutoSizer disableHeight>
            {({ width }) => (
              <List
                autoHeight
                width={width === 0 ? 0.1 : width}
                height={height}
                isScrolling={isScrolling}
                onScroll={onChildScroll}
                rowCount={displayItems.length}
                rowHeight={ROW_HEIGHT}
                scrollTop={scrollTop}
                overscanRowCount={3}
                ref={listRef}
                rowRenderer={({ index, key, style }) => (
                  <div key={key} style={style}>
                    <VerifyGalleryArtworkCard artworkData={displayItems[index]} />
                  </div>
                )}
              />
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    </>
  );
}
